import React from 'react';
import { MapPin } from 'lucide-react';
import { ConvergeMark, EvalentXEndorse } from './ConvergeLogo';
import { RegisterButton } from './RegisterButton';
import { Countdown } from './Countdown';
import { AddToCalendar } from './AddToCalendar';
import { VENUE, ADDRESS, FULL_ADDRESS, PARTNER } from './data';

const AaruushMark = () => (
  <span className="flex flex-col items-start gap-1.5" data-testid="hero-aaruush">
    <span className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-[0.22em] leading-none">In association with</span>
    <span className="inline-flex bg-[#111] border-2 border-[#111] px-2.5 py-1.5 shadow-[3px_3px_0_#FF6338]">
      <img src="/aaruush-logo.webp" alt={PARTNER} className="h-7 sm:h-8 w-auto" width="171" height="32" loading="eager" data-testid="hero-aaruush-logo" />
    </span>
  </span>
);

const Sticker = ({ children, className }) => (
  <span className={`absolute z-10 border-2 border-[#111] px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider shadow-[3px_3px_0_#111] ${className}`}>{children}</span>
);

const DateBlock = () => (
  <div className="relative bg-[#111] text-[#F5F3EB] border-2 border-[#111] shadow-[8px_8px_0_#365CFF] p-6 sm:p-8" data-testid="hero-date-block">
    <Sticker className="-top-4 -right-3 rotate-3 bg-[#D7FF38] text-[#111]">2 days · 24h hack</Sticker>
    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D7FF38]">November 2026</p>
    <div className="cv-display font-extrabold leading-[0.85] text-[96px] sm:text-[88px] lg:text-[128px] mt-2">16<span className="text-[#365CFF]">—</span><br />17</div>
    <p className="mt-4 text-xs font-bold uppercase tracking-widest">Mon → Tue</p>
    <div className="mt-6 pt-5 border-t border-[#F5F3EB]/25">
      <p className="text-[9px] font-extrabold uppercase tracking-[0.22em] text-[#D7FF38]">In association with</p>
      <img src="/aaruush-logo.webp" alt={PARTNER} className="mt-2 h-8 w-auto" width="171" height="32" loading="eager" data-testid="date-block-aaruush-logo" />
      <p className="mt-4 flex items-start gap-2 text-[11px] font-bold uppercase tracking-wider leading-snug" data-testid="date-block-address">
        <MapPin size={14} className="shrink-0 mt-px text-[#FF6338]" /><span>{FULL_ADDRESS}</span>
      </p>
    </div>
  </div>
);

export const ConvergeHero = () => (
  <section id="overview" className="relative cv-grid-bg border-b-2 border-[#111] overflow-hidden scroll-mt-20" data-testid="hero-section">
    <ConvergeMark size={420} variant="mono" className="absolute -right-24 -bottom-28 opacity-[0.06] pointer-events-none hidden md:block" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-14">
      <div className="flex flex-wrap items-center justify-between gap-4 cv-rise">
        <p className="inline-block bg-[#111] text-[#D7FF38] text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] px-3 py-2" data-testid="hero-eyebrow">
          National-level hackathon · AI workshop · HR conclave
        </p>
        <div className="flex flex-wrap items-end gap-3 sm:gap-4" data-testid="hero-branding">
          <EvalentXEndorse />
          <span className="w-0.5 self-stretch bg-[#111] hidden sm:block" aria-hidden="true" />
          <AaruushMark />
        </div>
      </div>
      <h1 className="cv-display font-extrabold uppercase leading-[0.85] mt-6 whitespace-nowrap text-[clamp(1.8rem,8.2vw,7.2rem)] flex items-center gap-[0.12em] cv-rise" data-testid="hero-title">
        Converge<ConvergeMark size={120} variant="accent" animated className="w-[0.7em] h-[0.7em] shrink-0" />
      </h1>
    <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
      <div className="lg:col-span-8 cv-rise">

        <div className="flex flex-wrap items-center gap-3">
          <p className="cv-display text-xl sm:text-3xl font-extrabold uppercase bg-[#D7FF38] border-2 border-[#111] px-3 py-1 -rotate-1" data-testid="hero-tagline">Ideas collide.</p>
          <p className="cv-display text-xl sm:text-3xl font-extrabold uppercase bg-[#365CFF] text-white border-2 border-[#111] px-3 py-1 rotate-1">Futures emerge.</p>
        </div>
        <p className="mt-7 max-w-2xl text-sm sm:text-base font-medium leading-relaxed" data-testid="hero-description">
          24 hours of building, experimentation, and innovation—bringing together developers, AI enthusiasts, entrepreneurs, industry leaders, and emerging talent.
        </p>
        <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-2xl">
          <div className="bg-[#D7FF38] border-2 border-[#111] p-4 shadow-[4px_4px_0_#111]" data-testid="hero-prize-pool">
            <p className="cv-display text-3xl font-extrabold">₹2,50,000</p>
            <p className="text-[11px] font-bold uppercase tracking-wider mt-1">Prize pool · cash & goodies</p>
          </div>
          <div className="bg-white border-2 border-[#111] p-4 shadow-[4px_4px_0_#111]" data-testid="hero-coupons">
            <p className="cv-display text-3xl font-extrabold">₹1,00,000</p>
            <p className="text-[11px] font-bold uppercase tracking-wider mt-1">Worth of career-development coupons*</p>
          </div>
        </div>
        <p className="mt-5 flex items-start gap-2 text-xs sm:text-sm font-medium max-w-2xl" data-testid="hero-address">
          <MapPin size={16} className="shrink-0 mt-0.5 text-[#FF6338]" /><span><b>{VENUE}</b> — {ADDRESS}</span>
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <RegisterButton size="lg" testId="hero-register-btn" />
          <a href="#prizes" className="cv-btn inline-flex items-center px-7 py-4 text-sm sm:text-base font-extrabold uppercase tracking-wider border-2 border-[#111] bg-white shadow-[4px_4px_0_#111]" data-testid="hero-explore-btn">Explore the experience ↓</a>
          <AddToCalendar />
        </div>
      </div>
      <div className="lg:col-span-4 grid sm:grid-cols-2 lg:grid-cols-1 gap-8 items-start cv-rise" style={{ animationDelay: '.15s' }}>
        <DateBlock />
        <Countdown />
      </div>
    </div>
    </div>
  </section>
);
